// src/components/Avaliacao/AvaliacaoList.jsx
import React, { useEffect, useState } from 'react';
import { listarAvaliacoes } from '../../services/avaliacaoService';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Divider,
  TextField
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';

// Imports para usar filtros de data
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ptBR from 'date-fns/locale/pt-BR';

const AvaliacaoList = () => {
  const [avaliacoes, setAvaliacoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtros de data
  const [dataInicio, setDataInicio] = useState(null);
  const [dataFim, setDataFim] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await listarAvaliacoes();
        // Caso o servidor retorne algum 'warning' ou 'error' no data, 
        // você pode tratar aqui se desejar.
        const avaliacoesData = Array.isArray(response.data)
          ? response.data
          : [response.data];

        // Filtra apenas avaliações que tenham um ID válido
        const avaliacoesValidas = avaliacoesData.filter((av) => av && av.id);
        setAvaliacoes(avaliacoesValidas);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar avaliações:', err);
        if (err.response?.data?.error?.includes('not associated')) {
          setError(
            'Algumas avaliações podem não estar completamente carregadas devido a problemas de associação.'
          );
        } else {
          setError('Erro ao carregar avaliações.');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Filtra as avaliações pelo período de última atualização (updatedAt ou updated_at).
   * - Se dataInicio existir, filtra avaliações com updatedAt >= dataInicio.
   * - Se dataFim existir, filtra avaliações com updatedAt <= dataFim.
   */
  const filtrarPorPeriodo = (lista) => {
    if (!dataInicio && !dataFim) return lista; // sem filtros de data

    return lista.filter((avaliacao) => {
      const dataAtual = new Date(avaliacao.updatedAt || avaliacao.updated_at);

      // Ajuste a dataInicio para começar à meia-noite (0h)
      if (dataInicio && dataAtual < new Date(dataInicio).setHours(0, 0, 0, 0)) {
        return false;
      }
      // Ajuste a dataFim para finalizar às 23:59
      if (dataFim && dataAtual > new Date(dataFim).setHours(23, 59, 59, 999)) {
        return false;
      }
      return true;
    });
  };

  // Aplicamos o filtro de período nas avaliações antes de exibir
  const avaliacoesFiltradas = filtrarPorPeriodo(avaliacoes);

  // Estados de loading e erro
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ m: 4 }}>
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Box>
    );
  }

  if (!avaliacoesFiltradas || avaliacoesFiltradas.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ m: 4, textAlign: 'center' }}
      >
        Nenhuma avaliação encontrada (verifique os filtros).
      </Typography>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Barra de Filtros por Data */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          mb: 3
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
          <DatePicker
            label="Data Início"
            value={dataInicio}
            onChange={(newValue) => setDataInicio(newValue)}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
          <DatePicker
            label="Data Fim"
            value={dataFim}
            onChange={(newValue) => setDataFim(newValue)}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        </LocalizationProvider>

        {/* Botão para limpar filtros */}
        <Button
          variant="outlined"
          onClick={() => {
            setDataInicio(null);
            setDataFim(null);
          }}
        >
          Limpar Filtros
        </Button>
      </Box>

      {/* Grid de Avaliações */}
      <Grid container spacing={3}>
      {avaliacoesFiltradas.map((avaliacao) => {
  // Calcular as notas por avaliador (Trabalho Escrito + Defesa Oral)
  const notaArguente = ((avaliacao.arguenteEscritoTotal || 0) + (avaliacao.arguenteOralTotal || 0))
    .toFixed(1);
  const notaPresidente = ((avaliacao.presidenteEscritoTotal || 0) + (avaliacao.presidenteOralTotal || 0))
    .toFixed(1);
  const notaSupervisor = ((avaliacao.supervisorEscritoTotal || 0) + (avaliacao.supervisorOralTotal || 0))
    .toFixed(1);

  return (
    <Grid item xs={12} sm={6} md={4} key={avaliacao.id}>
      <Card sx={{
        borderRadius: 3,
        boxShadow: 4,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: 8
        },
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}>
        <CardHeader
          avatar={
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <AssessmentIcon />
            </Avatar>
          }
          title={
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Defesa #{avaliacao.defesaId}
            </Typography>
          }
          subheader={
            <Typography variant="body2" color="white">
              Média Final: {avaliacao.mediaFinal}
            </Typography>
          }
          sx={{
            background: 'linear-gradient(45deg, #2196F3, #21CBF3)',
            color: 'white',
            p: 1,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8
          }}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          {/* Notas dos Avaliadores */}
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Nota Arguente:</strong> {notaArguente}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (E: {avaliacao.arguenteEscritoTotal || 0} + O: {avaliacao.arguenteOralTotal || 0})
            </Typography>
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Nota Presidente:</strong> {notaPresidente}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (E: {avaliacao.presidenteEscritoTotal || 0} + O: {avaliacao.presidenteOralTotal || 0})
            </Typography>
          </Typography>
          
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Nota Supervisor:</strong> {notaSupervisor}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              (E: {avaliacao.supervisorEscritoTotal || 0} + O: {avaliacao.supervisorOralTotal || 0})
            </Typography>
          </Typography>

          <Divider sx={{ my: 1 }} />
          
          {/* Totais */}
          <Typography variant="body2" sx={{ mb: 1 }}>
            <strong>Trabalho Escrito:</strong> {avaliacao.notaTrabalhoEscrito}
          </Typography>
          <Typography variant="body2">
            <strong>Defesa Oral:</strong> {avaliacao.notaDefesaOral}
          </Typography>
          
          <Divider sx={{ my: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Atualizado em: {new Date(avaliacao.updatedAt || avaliacao.updated_at).toLocaleString()}
          </Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => navigate(`/avaliacoes/${avaliacao.id}`)}
            sx={{ textTransform: 'none' }}
          >
            Ver Detalhes
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AvaliacaoList;
